import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const DonorProfileView = () => {
  const [searchParams] = useSearchParams();
  const [donor, setDonor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});

  const donorId = searchParams.get('id');
  const email = searchParams.get('email');
  const phone = searchParams.get('phone');

  useEffect(() => {
    fetchDonor();
  }, [donorId, email, phone]);

  const fetchDonor = async () => {
    console.log('[DonorProfileView] fetchDonor called with:', { donorId, email, phone });
    setLoading(true);
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/donors`;
      let fetchUrl = url;
      
      if (donorId) {
        fetchUrl = `${url}?id=eq.${donorId}`;
      } else if (email) {
        fetchUrl = `${url}?email=eq.${encodeURIComponent(email)}`;
      } else if (phone) {
        fetchUrl = `${url}?phone_number=eq.${encodeURIComponent(phone)}`;
      } else {
        console.log('[DonorProfileView] No search params found');
        setLoading(false);
        return;
      }

      console.log('[DonorProfileView] Fetching from:', fetchUrl);

      const response = await fetch(fetchUrl, {
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        }
      });

      console.log('[DonorProfileView] Response status:', response.status);

      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('[DonorProfileView] Fetched data:', data);

      if (!data || data.length === 0) {
        console.log('[DonorProfileView] No donor found');
        setDonor(null);
        setLoading(false);
        return;
      }

      const donorData = data[0];
      console.log('[DonorProfileView] Setting donor data:', donorData);
      setDonor(donorData);
      setFormData({
        name: donorData.name || '',
        email: donorData.email || '',
        phoneNumber: donorData.phone_number || '',
        location: donorData.location || '',
        district: donorData.district || '',
        bloodGroup: donorData.blood_group || '',
        age: donorData.age || '',
        weight: donorData.weight || '',
        isAvailable: donorData.is_available !== false,
      });
    } catch (error) {
      console.error('Error fetching donor:', error);
      toast.error('Could not load donor profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('donors')
        .update({
          name: formData.name,
          email: formData.email,
          phone_number: formData.phoneNumber,
          location: formData.location,
          district: formData.district,
          blood_group: formData.bloodGroup,
          age: parseInt(formData.age) || null,
          weight: parseFloat(formData.weight) || null,
          is_available: formData.isAvailable,
          updated_at: new Date().toISOString(),
        })
        .eq('id', donor.id);

      if (error) throw error;

      toast.success('Profile updated successfully!');
      setEditMode(false);
      fetchDonor();
    } catch (error) {
      console.error('Error updating donor:', error);
      toast.error('Failed to update profile');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-crimson-600 mx-auto"></div>
          <p className="mt-4 text-surface-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!donor) {
    return (
      <div className="min-h-screen bg-surface-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="card-elevated p-8 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-surface-900 mb-2">Profile Not Found</h2>
            <p className="text-surface-600 mb-6">
              We couldn't find your donor profile. Please register or check your details.
            </p>
            <Link to="/register-donor" className="btn btn-primary btn-md">
              Register as Donor
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Success Banner */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 mb-8 text-white shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-3xl">
              🎉
            </div>
            <div>
              <h1 className="text-2xl font-bold">Welcome to OCYM Blood Network!</h1>
              <p className="text-emerald-100">Thank you for registering as a blood donor. Your contribution can save lives.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Donor Profile Card */}
          <div className="lg:col-span-2">
            <div className="card-elevated p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-surface-900">Your Donor Profile</h2>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className="btn btn-ghost btn-sm"
                >
                  {editMode ? 'Cancel' : 'Edit Profile'}
                </button>
              </div>

              {editMode ? (
                <form onSubmit={handleUpdate} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="field-label">Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="field-label">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-field"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="field-label">Phone</label>
                      <input
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="field-label">Blood Group</label>
                      <input
                        type="text"
                        value={formData.bloodGroup}
                        disabled
                        className="input-field bg-surface-100"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="field-label">District</label>
                      <input
                        type="text"
                        value={formData.district}
                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="field-label">Location</label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="input-field"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="field-label">Age</label>
                      <input
                        type="number"
                        value={formData.age}
                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="field-label">Weight (kg)</label>
                      <input
                        type="number"
                        value={formData.weight}
                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                        className="input-field"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isAvailable"
                      checked={formData.isAvailable}
                      onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
                      className="w-4 h-4 text-crimson-600 rounded"
                    />
                    <label htmlFor="isAvailable" className="text-sm text-surface-700">
                      I am currently available to donate blood
                    </label>
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" className="btn btn-primary btn-md flex-1">
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditMode(false)}
                      className="btn btn-ghost btn-md flex-1 border border-surface-200"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-surface-50 rounded-xl">
                      <p className="text-sm text-surface-500">Blood Group</p>
                      <p className="text-2xl font-bold text-crimson-600">{donor.blood_group}</p>
                    </div>
                    <div className="p-4 bg-surface-50 rounded-xl">
                      <p className="text-sm text-surface-500">Status</p>
                      <p className={`text-lg font-semibold ${donor.is_available ? 'text-green-600' : 'text-amber-600'}`}>
                        {donor.is_available ? '✅ Available' : '⏸️ Not Available'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-surface-500">Name</p>
                      <p className="font-medium">{donor.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-surface-500">Email</p>
                      <p className="font-medium">{donor.email || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-surface-500">Phone</p>
                      <p className="font-medium">{donor.phone_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-surface-500">Location</p>
                      <p className="font-medium">{donor.location}, {donor.district}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-surface-500">Age</p>
                      <p className="font-medium">{donor.age} years</p>
                    </div>
                    <div>
                      <p className="text-sm text-surface-500">Weight</p>
                      <p className="font-medium">{donor.weight} kg</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Info Cards */}
          <div className="space-y-6">
            {/* Blood Donation Info */}
            <div className="card-elevated p-5">
              <h3 className="font-bold text-surface-900 mb-4 flex items-center gap-2">
                <span>🩸</span> About Blood Donation
              </h3>
              <ul className="space-y-3 text-sm text-surface-700">
                <li className="flex items-start gap-2">
                  <span className="text-crimson-500 mt-0.5">•</span>
                  <span>One donation can save up to 3 lives</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-crimson-500 mt-0.5">•</span>
                  <span>Donation takes only 10-15 minutes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-crimson-500 mt-0.5">•</span>
                  <span>Your body replenishes blood within 24-48 hours</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-crimson-500 mt-0.5">•</span>
                  <span>Minimum 8-week gap between donations</span>
                </li>
              </ul>
            </div>

            {/* Eligibility */}
            <div className="card-elevated p-5">
              <h3 className="font-bold text-surface-900 mb-4 flex items-center gap-2">
                <span>✅</span> Eligibility
              </h3>
              <ul className="space-y-2 text-sm text-surface-700">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Age 18-65 years</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Weight above 45kg</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Good health condition</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>No recent infections</span>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="card-elevated p-5 bg-gradient-to-br from-crimson-50 to-white">
              <h3 className="font-bold text-crimson-900 mb-3">📞 Need Help?</h3>
              <p className="text-sm text-crimson-700 mb-2">
                Contact OCYM coordinators for any assistance
              </p>
              <Link to="/" className="text-sm text-crimson-600 font-medium hover:underline">
                View Contact Information →
              </Link>
            </div>

            {/* Quick Actions */}
            <div className="space-y-3">
              <Link to="/request-blood" className="btn btn-primary btn-md w-full">
                🩸 Request Blood
              </Link>
              <Link to="/requests" className="btn btn-ghost btn-md w-full border border-surface-200">
                📋 View Blood Requests
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonorProfileView;
